import PropTypes from 'prop-types';

import { Box, Button } from '@mui/material';
import { styled } from '@mui/material/styles';

import GenericFormContainer from '../Forms/GenericFormContainer';
import AmenityInfoContainer from './AmenityInfoContainer';

import BackButtonComponent from '../Forms/BackButtonComponent';

import { isAdmin, isPM } from '../../utils';
import { lengthValidation500, required } from '../Forms/utils';
import {
  amenitiesReservationsDayAllowed, // amenitiesReservationsEndTimeAllowed,
  amenitiesReservationsStartTimeAllowed,
  onDateChangeFormatter,
} from './utils';

import {
  CUSTOM_PICKER_ROW,
  DATE_TIME_PICKER,
  REACT_SELECT_INPUT,
  STYLED_TEXT,
} from '../Forms/fieldTypes';
import Tooltip from '../Tooltip';

const StyledContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  width: '100%',

  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const StyledWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '45%',
  marginRight: '5%',

  [theme.breakpoints.down('md')]: {
    width: '100%',
    marginRight: 0,
  },
}));

const AmenityReservationFormComponent = (props) => {
  const {
    buildingOptions,
    amenityOptions,
    personOptions,
    selectedBuildingId,
    selectedAmenity,
    shouldDisableDate,
    onDayChange,
    startDate,
    // endDate,
    isPreview,
    setIsPreview,
    amenityId,
    isCreate,
    isOutOfAllowedDateRange,
    timezoneName,
    getDisabledHours,
    getDisabledMinutes,
    isExpired,
    form,
    onSubmit,
    onLoad,
    initialValues,
    asyncValidate,
    asyncChangeFields,
    shouldAsyncValidate,
    match,
    setReservationDate,
    reservationDate,
    setSelectedStartTime,
    setSelectedDuration,
    selectedStartTime,
    selectedDuration,
  } = props;

  const isDayTimePickerDisabled =
    !selectedBuildingId ||
    !selectedAmenity ||
    !selectedAmenity?.hasAvailableDates ||
    isOutOfAllowedDateRange;

  const isDayTimeValidationEnabled = !isPreview && !isOutOfAllowedDateRange;
  const timezoneInfo = `time is in ${timezoneName}`;

  let formData = [
    {
      keyName: 'building',
      label: 'Building',
      fieldType: REACT_SELECT_INPUT,
      options: buildingOptions,
      validate: [required],
      disabled: !isCreate || buildingOptions?.length === 1,
    },
    {
      keyName: 'id',
      label: 'Select Amenity',
      fieldType: REACT_SELECT_INPUT,
      options: amenityOptions,
      validate: [required],
      disabled: !selectedBuildingId || !isCreate,
    },
    {
      keyName: 'contactPerson',
      label: 'Requested for',
      fieldType: REACT_SELECT_INPUT,
      options: personOptions,
      validate: [],
      disabled: !selectedBuildingId || !selectedAmenity,
    },
    {
      keyName: 'startDate',
      label: (
        <Box width="fit-content">
          {timezoneName ? (
            <Tooltip tooltip={`Start ${timezoneInfo}`}>
              Reservation Start
            </Tooltip>
          ) : (
            `Reservation Start`
          )}
        </Box>
      ),
      fieldType: DATE_TIME_PICKER,
      validate: isDayTimeValidationEnabled
        ? [
            required,
            amenitiesReservationsDayAllowed,
            amenitiesReservationsStartTimeAllowed,
          ]
        : [],
      disabled: isDayTimePickerDisabled,
      // date-picker props
      disablePast: true,
      onChange: onDayChange,
      onChangeFormatter: onDateChangeFormatter,
      shouldDisableDate,
      // time-picker props
      disabledTime: !startDate,
      getDisabledHours,
      getDisabledMinutes,
      showTimePicker: false,
      setReservationDate,
      style: {
        width: 'fit-content',
      },
    },
    // {
    //   keyName: 'endDate',
    //   label: (
    //     <Box width="fit-content">
    //       {timezoneName ? (
    //         <Tooltip tooltip={`End ${timezoneInfo}`}>Reservation End</Tooltip>
    //       ) : (
    //         `Reservation End`
    //       )}
    //     </Box>
    //   ),
    //   fieldType: DATE_TIME_PICKER,
    //   validate: isDayTimeValidationEnabled
    //     ? [required, amenitiesReservationsEndTimeAllowed]
    //     : [],
    //   disabled: isDayTimePickerDisabled,
    //   // date-picker props
    //   disablePast: true,
    //   onChange: onDayChange,
    //   onChangeFormatter: onDateChangeFormatter,
    //   shouldDisableDate,
    //   // time-picker props
    //   disabledTime: !endDate,
    //   getDisabledHours,
    //   getDisabledMinutes,
    // },
    {
      keyName: 'startTime',
      label: 'Start Time',
      fieldType: CUSTOM_PICKER_ROW,
      validate: [required],
      selectedAmenity,
      reservationDate,
      isStartTime: true,
      setSelectedStartTime,
      selectedStartTime,
      isDayTimePickerDisabled,
      isHidden: !reservationDate,
      isCreate,
    },
    {
      keyName: 'reservationLength',
      label: 'Reservation length',
      fieldType: CUSTOM_PICKER_ROW,
      validate: [required],
      selectedAmenity,
      reservationDate,
      setSelectedDuration,
      selectedDuration,
      isDayTimePickerDisabled,
      isHidden: !reservationDate,
      isCreate,
    },
    {
      keyName: 'comments',
      label: 'Comment',
      fieldType: STYLED_TEXT,
      multiline: true,
      validate: [lengthValidation500],
      disabled: !selectedBuildingId || !selectedAmenity,
    },
  ];

  if (isPreview) {
    formData = formData.map((field) => ({ ...field, disabled: true }));
  }

  const CustomButtons = () =>
    isPreview &&
    (isAdmin() || isPM(match?.params)) && (
      <Box display="flex">
        <BackButtonComponent />
        <Box ml="2rem" />
        {isExpired ? null : (
          <Button
            variant="contained"
            color="primary"
            disableElevation
            onClick={() => setIsPreview(false)}
          >
            Edit
          </Button>
        )}
      </Box>
    );

  return (
    <StyledContainer>
      <StyledWrapper>
        <GenericFormContainer
          title={`${isCreate ? 'Add' : 'Edit'} Amenity Reservation`}
          formData={formData}
          isHideSubmitButton={isPreview}
          canBeUpdated={!isPreview}
          backButton={!isPreview}
          CustomComponent={CustomButtons}
          disabled={isPreview}
          submitText="Save Reservation"
          isFullWidthForm
          form={form}
          onSubmit={onSubmit}
          onLoad={onLoad}
          initialValues={initialValues}
          enableReinitialize
          asyncValidate={asyncValidate}
          asyncChangeFields={asyncChangeFields}
          shouldAsyncValidate={shouldAsyncValidate}
          selectedAmenity={selectedAmenity}
        />
      </StyledWrapper>
      {selectedAmenity && (
        <StyledWrapper>
          <AmenityInfoContainer
            amenityId={amenityId}
            timezoneName={timezoneName}
            match={match}
          />
        </StyledWrapper>
      )}
    </StyledContainer>
  );
};

AmenityReservationFormComponent.defaultProps = {
  buildingOptions: [],
  amenityOptions: [],
  personOptions: [],
  selectedBuildingId: null,
  selectedAmenity: null,
  startDate: null,
  // endDate: null,
  amenityId: null,
  isExpired: true,
  isOutOfAllowedDateRange: false,
  timezoneName: '',
  getDisabledHours: () => {},
  getDisabledMinutes: () => {},
  asyncValidate: () => {},
  asyncChangeFields: [],
  shouldAsyncValidate: () => {},
};

AmenityReservationFormComponent.propTypes = {
  buildingOptions: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  amenityOptions: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  personOptions: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  selectedBuildingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedAmenity: PropTypes.shape({
    hasAvailableDates: PropTypes.bool,
  }),
  shouldDisableDate: PropTypes.func.isRequired,
  onDayChange: PropTypes.func.isRequired,
  startDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Object),
  ]),
  // endDate: PropTypes.oneOfType([
  //   PropTypes.string,
  //   PropTypes.instanceOf(Object),
  // ]),
  isPreview: PropTypes.bool.isRequired,
  setIsPreview: PropTypes.func.isRequired,
  amenityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isCreate: PropTypes.bool.isRequired,
  isExpired: PropTypes.bool,
  isOutOfAllowedDateRange: PropTypes.bool,
  timezoneName: PropTypes.string,
  getDisabledHours: PropTypes.func,
  getDisabledMinutes: PropTypes.func,
  form: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.instanceOf(Object).isRequired,
  onLoad: PropTypes.func.isRequired,
  asyncValidate: PropTypes.func,
  asyncChangeFields: PropTypes.instanceOf(Array),
  shouldAsyncValidate: PropTypes.func,
  match: PropTypes.instanceOf(Object).isRequired,
  setReservationDate: PropTypes.func,
  reservationDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Array),
  ]),
  setSelectedStartTime: PropTypes.func,
  setSelectedDuration: PropTypes.func,
  selectedStartTime: PropTypes.number,
  selectedDuration: PropTypes.number,
};

export default AmenityReservationFormComponent;
